import React, { useState } from 'react';
import { Calculator, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link } from 'react-router-dom';

const MortgageWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);

    // Calculator State
    const [amount, setAmount] = useState(150000);
    const [years, setYears] = useState(25);
    const [rate, setRate] = useState(7.5);

    // Calculate Monthly Payment
    const calculatePayment = () => {
        const principal = amount;
        const monthlyRate = rate / 100 / 12;
        const numberOfPayments = years * 12;

        if (rate === 0) return principal / numberOfPayments;

        const payment =
            (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
            (Math.pow(1 + monthlyRate, numberOfPayments) - 1);

        return payment;
    };

    const monthlyPayment = calculatePayment();

    if (!isOpen) {
        return (
            <button
                onClick={() => setIsOpen(true)}
                title="Calculadora hipotecaria"
                className="fixed bottom-6 right-6 sm:bottom-24 sm:right-6 z-50 bg-white text-primary p-2 sm:p-3 rounded-full shadow-xl border border-gray-200 hover:scale-110 transition-transform flex items-center gap-2 group"
            >
                <div className="bg-secondary text-white p-1.5 sm:p-2 rounded-full">
                    <Calculator size={18} className="sm:w-5 sm:h-5" />
                </div>
                <span className="font-bold pr-2 hidden group-hover:inline-block transition-all text-sm">
                    Calculadora
                </span>
            </button>
        );
    }

    return (
        <div className={`fixed bottom-6 right-4 sm:bottom-24 sm:right-6 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 w-[calc(100vw-32px)] sm:w-80 max-w-80 transition-all duration-300 overflow-hidden ${isMinimized ? 'h-14' : 'h-auto max-h-[70vh] overflow-y-auto'}`}>
            {/* Header */}
            <div className="bg-primary text-white p-3 flex justify-between items-center cursor-pointer" onClick={() => setIsMinimized(!isMinimized)}>
                <div className="flex items-center gap-2">
                    <Calculator size={18} className="text-secondary" />
                    <span className="font-bold text-sm">Calculadora de Cuota</span>
                </div>
                <div className="flex items-center gap-1">
                    {isMinimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    <button onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} className="hover:bg-white/20 rounded p-0.5">
                        <X size={16} />
                    </button>
                </div>
            </div>

            {/* Body */}
            {!isMinimized && (
                <div className="p-5 space-y-5">
                    {/* Result */}
                    <div className="text-center bg-gray-50 p-3 rounded-xl border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Cuota Mensual Estimada</p>
                        <p className="text-3xl font-bold text-secondary">
                            ${monthlyPayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                    </div>

                    {/* Sliders */}
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs text-gray-600">Monto del Préstamo ($)</Label>
                            <Input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(Number(e.target.value))}
                                className="h-8 text-sm font-bold text-primary"
                            />
                            <Slider
                                value={[amount]}
                                min={5000}
                                max={500000}
                                step={1000}
                                onValueChange={(val) => setAmount(val[0])}
                                className="cursor-pointer"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-gray-600">Plazo (Años)</Label>
                            <Input
                                type="number"
                                value={years}
                                onChange={(e) => setYears(Number(e.target.value))}
                                className="h-8 text-sm font-bold text-primary"
                            />
                            <Slider
                                value={[years]}
                                min={1}
                                max={30}
                                step={1}
                                onValueChange={(val) => setYears(val[0])}
                                className="cursor-pointer"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs text-gray-600">Tasa de Interés (%)</Label>
                            <Input
                                type="number"
                                value={rate}
                                onChange={(e) => setRate(Number(e.target.value))}
                                step="0.1"
                                className="h-8 text-sm font-bold text-primary"
                            />
                            <Slider
                                value={[rate]}
                                min={1}
                                max={20}
                                step={0.1}
                                onValueChange={(val) => setRate(val[0])}
                                className="cursor-pointer"
                            />
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="pt-2">
                        <Link to="/centro-financiero">
                            <Button variant="outline" size="sm" className="w-full text-xs border-secondary text-secondary hover:bg-secondary hover:text-white">
                                Ver Análisis Completo
                            </Button>
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MortgageWidget;
